import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Row,
  TimePicker,
  Tooltip,
  theme,
} from 'antd';
import {
  ClearOutlined,
  DeleteOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'util/dayjs';
import React, { Ref, useEffect, useRef, useState } from 'react';
import { TimeWindow, JourneyState } from 'interfaces/journey';
import { useSetJourneySchedule } from 'services/journeys';
import { suggestedScheduleEnd } from 'services/journeys/shipmentHelpers';
import { useJourneyDashboardContext } from '../context';

interface ScheduleSelectorProps {
  disabled?: boolean;
}

const ScheduleSelector = React.forwardRef<
  HTMLDivElement,
  ScheduleSelectorProps
>(({ disabled }, ref) => {
  const { token } = theme.useToken();
  const { id, schedule, issue, state } = useJourneyDashboardContext();
  const [date, setDate] = useState<dayjs.Dayjs | null>(null);
  const [timeRange, setTimeRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const { mutate: setSchedule, isPending } = useSetJourneySchedule();
  const saveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const persistedRef = useRef<string | null>(null);

  const { date: savedDate, from: savedFrom, to: savedTo } = schedule ?? {};
  useEffect(() => {
    if (!savedDate || !savedFrom || !savedTo) return;
    persistedRef.current = `${savedDate}|${savedFrom}|${savedTo}`;
    setDismissed(null);
    setDate(dayjs(savedDate));
    setTimeRange([
      dayjs(`${savedDate} ${savedFrom}`),
      dayjs(`${savedDate} ${savedTo}`),
    ]);
  }, [savedDate, savedFrom, savedTo]);

  useEffect(() => {
    const [from, to] = timeRange ?? [];
    if (!id || !date || !from || !to || isPending) return undefined;

    const fromStr = from.format('HH:mm');
    const toStr = to.format('HH:mm');
    const dateStr = date.format('YYYY-MM-DD');
    if (fromStr >= toStr) return undefined;

    const key = `${dateStr}|${fromStr}|${toStr}`;
    if (key === persistedRef.current) return undefined;

    saveTimeoutRef.current = setTimeout(() => {
      const payload: TimeWindow = {
        date: dateStr,
        from: fromStr,
        to: toStr,
        utcOffset: dayjs().format('Z'),
      };
      persistedRef.current = key;
      setSchedule(
        { journeyId: id, schedule: payload },
        {
          onError: () => {
            persistedRef.current = null;
          },
        },
      );
    }, 500);

    return () => clearTimeout(saveTimeoutRef.current);
  }, [date, timeRange, id, isPending, setSchedule]);

  const handleDateChange = (newDate: dayjs.Dayjs | null) => {
    setDate(newDate);
  };

  const handleTimeRangeChange = (
    newTimeRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
  ) => {
    setTimeRange(newTimeRange);
  };

  const handleClear = () => {
    if (!id) return;
    clearTimeout(saveTimeoutRef.current);
    setDate(null);
    setTimeRange(null);
    persistedRef.current = null;
    setSchedule({ journeyId: id, schedule: null });
  };

  const hasSchedule = !!date || !!timeRange?.[0];

  const shouldShowSuggestion = () => {
    if (state !== JourneyState.Draft || !schedule || !issue) return false;
    if (issue.blame !== 'schedule' || !issue.exceededBySeconds) return false;
    const suggested = suggestedScheduleEnd(schedule, issue);
    return !!suggested && suggested !== dismissed;
  };

  const suggested = shouldShowSuggestion()
    ? suggestedScheduleEnd(schedule!, issue!)
    : null;

  const disabledDate = (current: dayjs.Dayjs) => {
    return current && current.isBefore(dayjs(), 'day');
  };

  const disabledTime = (now: dayjs.Dayjs) => {
    if (!date || !date.isSame(dayjs(), 'day')) {
      return { disabledHours: () => [], disabledMinutes: () => [] };
    }

    const currentHour = dayjs().hour();
    const currentMinute = dayjs().minute();

    const disabledHours = Array.from({ length: currentHour }, (_, i) => i);
    const disabledMinutes =
      now.hour() === currentHour
        ? Array.from({ length: currentMinute + 1 }, (_, i) => i)
        : [];

    return {
      disabledHours: () => disabledHours,
      disabledMinutes: () => disabledMinutes,
    };
  };

  return (
    <Row style={{ width: '100%' }} ref={ref as Ref<HTMLDivElement>}>
      <Col xs={24}>
        <Card
          bodyStyle={{
            padding: '10px',
          }}
        >
          <Flex vertical>
            <Flex
              justify="space-between"
              align="center"
              style={{ margin: '-8px 8px 0 8px', color: 'darkgray' }}
            >
              <Flex gap="xs" align="center">
                <p style={{ margin: 0 }}>Horario</p>
                {issue?.blame === 'schedule' && issue.exceededBySeconds && (
                  <Tooltip title={issue.reasonDescription}>
                    <WarningOutlined
                      style={{ color: token.colorWarning, marginLeft: '4px' }}
                    />
                  </Tooltip>
                )}
              </Flex>
              {!suggested && (
                <Button
                  type="link"
                  size="small"
                  icon={<ClearOutlined />}
                  disabled={disabled || isPending || !hasSchedule}
                  onClick={handleClear}
                  style={{ height: 'auto', padding: 0 }}
                >
                  Limpiar
                </Button>
              )}
              {suggested && (
                <Flex gap="2px">
                  <Button
                    type="text"
                    size="small"
                    onClick={() => setDismissed(suggested)}
                    style={{ height: 'auto' }}
                  >
                    <DeleteOutlined style={{ color: token.colorError }} />
                  </Button>
                  <Button
                    type="text"
                    size="small"
                    loading={isPending}
                    onClick={() => {
                      if (!schedule || !id) return;
                      setSchedule(
                        {
                          journeyId: id,
                          schedule: { ...schedule, to: suggested },
                        },
                        {
                          onSuccess: () => setDismissed(null),
                          onError: () => setDismissed(null),
                        },
                      );
                    }}
                    style={{
                      height: 'auto',
                      fontSize: '12px',
                      color: token.colorWarning,
                    }}
                  >
                    Aplicar
                  </Button>
                </Flex>
              )}
            </Flex>
            <Flex gap="small">
              <DatePicker
                value={date}
                onChange={handleDateChange}
                disabledDate={disabledDate}
                disabled={disabled}
                format="DD/MM/YYYY"
                style={{ flex: 1 }}
              />
              {suggested ? (
                <div
                  style={{
                    flex: 1,
                    padding: '4px 11px',
                    border: `1px dashed ${token.colorWarning}`,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    background: token.colorBgContainer,
                    fontSize: '14px',
                    color: token.colorText,
                  }}
                  title={issue?.reasonDescription}
                >
                  {schedule?.from} →
                  <b style={{ color: token.colorWarning, paddingLeft: '4px' }}>
                    {' '}
                    {suggested}
                  </b>
                </div>
              ) : (
                <TimePicker.RangePicker
                  value={timeRange}
                  onChange={handleTimeRangeChange}
                  disabledTime={disabledTime}
                  disabled={disabled}
                  format="HH:mm"
                  style={{ flex: 1 }}
                  suffixIcon={
                    isPending ? (
                      <LoadingOutlined style={{ color: token.colorPrimary }} />
                    ) : undefined
                  }
                />
              )}
            </Flex>
          </Flex>
        </Card>
      </Col>
    </Row>
  );
});

ScheduleSelector.displayName = 'ScheduleSelector';

export default ScheduleSelector;
